/** @format */

import { EditorContent } from "@tiptap/react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader,
  Plus,
  ScanSearch,
  Save,
  Trash2,
  ArrowRight,
  Lock,
} from "lucide-react";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { EnhancedEditorToolbar } from "../../components/editor/EnhancedEditorToolbar";
import { LinkBubbleMenu } from "../../components/editor/LinkBubbleMenu";
import { ReferencePanel } from "../../components/sop/ReferencePanel";
import Header from "../../components/ui/Header";
import ProcessingStatus from "../../components/ui/ProcessingStatus";
import { useEditorConfig } from "../../hooks/useEditorConfig";
import { useImageHandling } from "../../hooks/useImageHandling";
import useProcessingStatus from "../../hooks/useProcessingStatus";
import { useToast } from "../../hooks/useToast";
import { useOrgRole } from "../../hooks/useOrgRole";
import { FLAG_RMA_ANALYSIS, useFeatureFlagEnabled } from "../../lib/featureFlags";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import { useSopStore } from "../../store/useSopStore";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import { useTrackChangesState } from "../../store/useTrackChangesStore";
import {
  ProcessingStatusEnum,
  TitlePageContent,
  type RevisionRow,
  type TitlePageMaterial,
  type VersionListItem,
} from "../../types/sop";
import {
  extractImageReferences,
  replaceImageReferences,
} from "../../utils/imageReferences";
import {
  extractAndUploadTitlePageImages,
  extractCoverImageUrl,
  replaceCoverPlaceholderForReadOnly,
} from "../../utils/titlePageImageProcessor";
import SopAccessDenied from "../../components/sop/SopAccessDenied";
import { FeedbackBubbleMenu } from "./FeedbackBubbleMenu";
import { TITLE_PAGE_FEEDBACK_STEP_INDEX } from "./feedbackStepIndex";
import { setFeedbackHighlights } from "./FeedbackHighlightExtension";

const SOPTitle: FC = () => {
  const { sopId } = useParams<{ sopId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const versionIdParam = searchParams.get("versionId");
  const feedbackIdParam = searchParams.get("feedbackId");
  const location = useLocation();
  const fromPendingVerification = location.state?.fromPendingVerification;
  const { showToast } = useToast();
  const { canEdit, canAccessRMA } = useOrgRole();
  // WORKAROUND: see lib/WORKAROUND_feature_flags.md
  const rmaEnabled = useFeatureFlagEnabled(FLAG_RMA_ANALYSIS);
  const { isSuperAdmin } = useSuperAdmin();

  const {
    setSelectedSOP,
    latestVersion,
    selectedVersion,
    getSelectedSOP,
    getSopById,
    hasUnsavedChanges,
    refreshSelectedSOP,
    getVersionList,
  } = useTrackChangesState();

  const {
    fetchSOPById,
    updateSopTitleById,
    updateSopTitlePage,
    updateSopBom,
    updateSopRevisions,
    createNewDraft,
    error: sopError,
  } = useSopStore();

  const versionList = getVersionList(sopId ?? "");

  const {
    imageUrls,
    setIsLoadingImages,
    getImageUrls,
    isValidImageRefId,
    normalizeImageRefId,
    setImageUrls,
    getCurrentUrl,
  } = useImageHandling();

  const selectedSOP = getSelectedSOP();
  const isEditable = selectedSOP?.isLatestVersionDraft() ?? true;
  const versionInContext = selectedVersion ?? latestVersion;
  const isViewingDoneVersion =
    versionInContext != null && versionInContext.status !== "draft";
  const hasDraftVersion = versionList.some((v) => v.status === "draft");
  const draftVersionId = versionList.find(
    (v) => v.status === "draft",
  )?.versionId;
  const previousVersionIdRef = useRef<string | null>(null);
  const consumedStepEntryLocationKeyRef = useRef<string | null>(null);

  const [editorContent, setEditorContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localMaterials, setLocalMaterials] = useState<TitlePageMaterial[]>([]);
  const [localRevisionRows, setLocalRevisionRows] = useState<RevisionRow[]>([]);
  const [titlePageAsideTab, setTitlePageAsideTab] = useState<
    "materials" | "revisions" | "versions"
  >("versions");
  const [versionSwitchLoading, setVersionSwitchLoading] = useState(false);
  const [isCreatingNewDraft, setIsCreatingNewDraft] = useState(false);
  const processingStatus = useProcessingStatus(sopId || "", selectedSOP);

  const versionsNewestFirst = useMemo(() => {
    if (versionList.length === 0) return [];
    return [...versionList].sort((a, b) => b.versionNumber - a.versionNumber);
  }, [versionList]);

  const fetchFeedbackForVersion = useFeedbackStore(
    (s) => s.fetchFeedbackForVersion,
  );
  const feedbackByVersion = useFeedbackStore((s) => s.feedbackByVersion);
  const setSelectedFeedbackIdForStep = useFeedbackStore(
    (s) => s.setSelectedFeedbackIdForStep,
  );
  const feedbackByStep = useFeedbackStore((s) => s.feedbackByStep);
  const selectedFeedbackIdByStep = useFeedbackStore(
    (s) => s.selectedFeedbackIdByStep,
  );
  const setOrphanedFeedbackIds = useFeedbackStore(
    (s) => s.setOrphanedFeedbackIds,
  );

  useEffect(() => {
    if (!sopId || !versionInContext?.versionId) return;
    const versionKey = `${sopId}:${versionInContext.versionId}`;
    const cameFromStepView = Boolean(location.state?.fromStepView);
    const shouldForceFromStepView =
      cameFromStepView &&
      consumedStepEntryLocationKeyRef.current !== location.key;
    const switchedBackToDraft =
      versionInContext.status === "draft" &&
      previousVersionIdRef.current != null &&
      previousVersionIdRef.current !== versionInContext.versionId;
    const hasCachedFeedback = feedbackByVersion[versionKey] !== undefined;

    if (!shouldForceFromStepView && !switchedBackToDraft && hasCachedFeedback) {
      previousVersionIdRef.current = versionInContext.versionId;
      return;
    }

    if (shouldForceFromStepView) {
      consumedStepEntryLocationKeyRef.current = location.key;
    }

    fetchFeedbackForVersion(sopId, versionInContext.versionId);
    previousVersionIdRef.current = versionInContext.versionId;
  }, [
    sopId,
    versionInContext?.versionId,
    versionInContext?.status,
    feedbackByVersion,
    location.key,
    location.state,
    fetchFeedbackForVersion,
  ]);

  const handleVersionSelect = useCallback(
    async (versionId: string) => {
      if (!sopId || selectedVersion?.versionId === versionId) return;
      setVersionSwitchLoading(true);
      const next = new URLSearchParams(searchParams);
      next.set("versionId", versionId);
      setSearchParams(next, { replace: true });
      // Version fetch is handled by the URL-driven load effect.
      setVersionSwitchLoading(false);
    },
    [
      sopId,
      selectedVersion?.versionId,
      searchParams,
      setSearchParams,
    ],
  );

  const editorContentRef = useRef("");
  const loadedImageIdsRef = useRef<Set<string>>(new Set());
  const loadedSopIdRef = useRef<string | null>(null);
  const loadedVersionIdRef = useRef<string | null>(null);
  const fetchedSopRequestKeyRef = useRef<string | null>(null);

  const handleEditorUpdate = useCallback((content: string) => {
    editorContentRef.current = content;
    setEditorContent(content);
  }, []);

  // Create a synchronous version of getCurrentUrl for the editor
  const getCurrentUrlSync = useCallback(
    (imageId: string): string | undefined => {
      const urlState = imageUrls[imageId];
      if (urlState && urlState.expiresAt > Date.now()) {
        return urlState.url;
      }
      return undefined;
    },
    [imageUrls],
  );

  const canEditContent = canEdit;

  const editor = useEditorConfig(
    editorContent,
    handleEditorUpdate,
    undefined,
    getCurrentUrlSync,
    canEditContent,
  );

  const stepSegments = useMemo(() => {
    const steps = versionInContext?.steps ?? latestVersion?.steps ?? [];
    return steps
      .filter(
        (
          s,
        ): s is typeof s & {
          sourceVideoId: string;
          sourceSegment: NonNullable<typeof s.sourceSegment>;
        } => !!s.sourceVideoId && !!s.sourceSegment,
      )
      .map((s) => ({
        videoId: s.sourceVideoId!,
        segment: s.sourceSegment!,
        title: s.title,
      }));
  }, [versionInContext?.steps, latestVersion?.steps]);

  // Load title page content for the viewed version. When switching version, always re-load.
  useLayoutEffect(() => {
    const sameSop = loadedSopIdRef.current === sopId;
    const sameVersion =
      loadedVersionIdRef.current === versionInContext?.versionId;
    if (!selectedSOP || !versionInContext || !editor || isSaving) return;
    if (sameSop && sameVersion && originalContent && isEditable) return;
    loadedSopIdRef.current = sopId ?? null;
    loadedVersionIdRef.current = versionInContext.versionId;
    const detailsHtml = versionInContext.titlePage?.details?.trim() || "";

    if (detailsHtml) {
      const versionIdForThisLoad = versionInContext.versionId;
      const processContent = async () => {
        let processedContent: string;
        try {
          processedContent = await replaceImageReferences(
            detailsHtml,
            getCurrentUrl,
          );
        } catch (error) {
          console.error("Error processing title page images:", error);
          processedContent = detailsHtml;
        }
        if (!isEditable) {
          processedContent =
            replaceCoverPlaceholderForReadOnly(processedContent);
        }
        setTimeout(() => {
          if (loadedVersionIdRef.current !== versionIdForThisLoad) return;
          editor.commands.setContent(processedContent, { emitUpdate: false });
          editor.commands.insertContentAt(editor.state.doc.content.size, {
            type: "paragraph",
          });
          if (isEditable) editor.commands.focus();
          setEditorContent(editor.getHTML());
          setOriginalContent(editor.getHTML());
        }, 0);
      };
      processContent();
      return;
    }

    // No title page details: editable = placeholder, read-only = message
    if (!isEditable) {
      const versionIdForThisLoad = versionInContext.versionId;
      const readOnlyMessage = [
        {
          type: "paragraph",
          attrs: {
            textAlign: "left",
            style: "font-size: 1rem; color: #94a3b8;",
          },
          content: [
            {
              type: "text",
              text: "No title page content for this version.",
              marks: [],
            },
          ],
        },
      ];
      setTimeout(() => {
        if (loadedVersionIdRef.current !== versionIdForThisLoad) return;
        editor.commands.setContent(readOnlyMessage, { emitUpdate: false });
        setEditorContent(editor.getHTML());
        setOriginalContent(editor.getHTML());
      }, 0);
      return;
    }

    const fullContent = [
      {
        type: "paragraph",
        attrs: {
          textAlign: "center",
          style:
            "font-size: 3rem; font-weight: bold; margin-bottom: 1rem; color: #3b82f6;",
        },
        content: [
          {
            type: "text",
            text: selectedSOP.getDisplayTitle(),
            marks: [{ type: "bold" }],
          },
        ],
      },
      {
        type: "paragraph",
        attrs: {
          textAlign: "center",
          style: "font-size: 1.2rem; color: #94a3b8;",
        },
        content: [
          {
            type: "text",
            text: "Standard Operating Procedure",
            marks: [{ type: "italic" }],
          },
        ],
      },
      {
        type: "imagePlaceholder",
        attrs: {
          id: `cover-placeholder-${Date.now()}`,
          text: "Add a cover image to the SOP",
        },
      },
    ];

    const versionIdForThisLoad = versionInContext.versionId;
    setTimeout(() => {
      if (loadedVersionIdRef.current !== versionIdForThisLoad) return;
      editor.commands.setContent(fullContent, { emitUpdate: false });
      const endPos = editor.state.doc.content.size;
      editor.commands.insertContentAt(endPos, { type: "paragraph" });
      editor.commands.focus();
      const finalContent = editor.getHTML();
      setEditorContent(finalContent);
      setOriginalContent(finalContent);
    }, 0);
  }, [
    sopId,
    selectedSOP,
    versionInContext,
    editor,
    isSaving,
    originalContent,
    isEditable,
    getCurrentUrl,
  ]);

  // Load images for title page content
  useEffect(() => {
    if (!sopId) return;
    loadedImageIdsRef.current.clear();
  }, [sopId]);

  const loadTitlePageImages = useCallback(async () => {
    const details = versionInContext?.titlePage?.details;
    if (!details) return;

    setIsLoadingImages(true);
    try {
      // Extract image references from title page content
      const titlePageRefs = extractImageReferences(details);

      const allImageRefIds = titlePageRefs.filter((id): id is string => {
        const isValid = Boolean(id) && isValidImageRefId(id);
        if (!isValid && id) {
          console.error("Invalid image reference ID:", id);
        }
        return isValid;
      });

      const uniqueImageRefIds = [
        ...new Set(allImageRefIds.map(normalizeImageRefId)),
      ].filter((id) => id !== "");

      const newImageIds = uniqueImageRefIds.filter(
        (id) => !loadedImageIdsRef.current.has(id) && !imageUrls[id],
      );

      if (newImageIds.length > 0) {
        newImageIds.forEach((id) => loadedImageIdsRef.current.add(id));
        const urls = await getImageUrls(newImageIds);
        setImageUrls((prev) => ({ ...prev, ...urls }));
      }
    } catch (error) {
      console.error("Error loading title page images:", error);
    } finally {
      setIsLoadingImages(false);
    }
  }, [
    versionInContext?.titlePage?.details,
    getImageUrls,
    setImageUrls,
    imageUrls,
    isValidImageRefId,
    normalizeImageRefId,
    setIsLoadingImages,
  ]);

  useEffect(() => {
    loadTitlePageImages();
  }, [loadTitlePageImages]);

  // Reprocess content when images are loaded. Never overwrite if user has local edits.
  useEffect(() => {
    if (hasChanges) return;
    if (originalContent && editorContentRef.current !== originalContent) return;
    const details = versionInContext?.titlePage?.details;
    if (!details || !editor || Object.keys(imageUrls).length === 0) return;
    const reprocessContent = async () => {
      try {
        const processedContent = await replaceImageReferences(
          details,
          getCurrentUrl,
        );
        editor.commands.setContent(processedContent, { emitUpdate: false });
        setEditorContent(editor.getHTML());
      } catch (error) {
        console.error("Error reprocessing title page images:", error);
      }
    };
    reprocessContent();
  }, [
    versionInContext?.titlePage?.details,
    editor,
    imageUrls,
    getCurrentUrl,
    hasChanges,
    originalContent,
  ]);

  useEffect(() => {
    if (!originalContent) return;
    setHasChanges(editorContentRef.current !== originalContent);
  }, [editorContent, originalContent]);

  const handleImageUpload = useCallback(async () => {
    if (!editor) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          const localUrl = URL.createObjectURL(file);

          editor.commands.setCustomImage({
            src: localUrl,
            alt: file.name || "Uploaded image",
            "data-image-id": `local-${Date.now()}`,
          });

          showToast("Success", "Image added locally", "success");
        } catch (error) {
          console.error("Error adding local image:", error);
          showToast("Error", "Failed to add image. Please try again.", "error");
        }
      }
    };
    input.click();
  }, [editor, showToast]);

  useEffect(() => {
    if (!sopId) return;
    setSelectedSOP(sopId);
    if (hasUnsavedChanges) return;

    const existingSop = getSopById(sopId);
    const needsFullSop =
      !existingSop ||
      (existingSop.getLatestVersion() &&
        (existingSop.getLatestVersion()!.steps?.length ?? 0) === 0 &&
        existingSop.getOperationCount() > 0);
    const needVersionFromUrl =
      Boolean(versionIdParam) &&
      (!existingSop || versionInContext?.versionId !== versionIdParam);
    const requestKey = needVersionFromUrl && versionIdParam
      ? `version:${sopId}:${versionIdParam}`
      : needsFullSop
        ? `full:${sopId}`
        : null;

    const applyDefaultVersionToUrl = (id: string) => {
      if (searchParams.get("versionId") === id) return;
      const next = new URLSearchParams(searchParams);
      next.set("versionId", id);
      setSearchParams(next, { replace: true });
    };

    if (needVersionFromUrl && versionIdParam) {
      if (fetchedSopRequestKeyRef.current === requestKey) return;
      fetchedSopRequestKeyRef.current = requestKey;
      fetchSOPById(sopId, versionIdParam, { refreshVersionList: false })
        .then(() => {
          refreshSelectedSOP();
        })
        .catch(() => {
          fetchSOPById(sopId).then(() => {
            refreshSelectedSOP();
            const latest = getSopById(sopId)?.getLatestVersion()?.versionId;
            if (latest) applyDefaultVersionToUrl(latest);
          });
        });
      return;
    }

    if (needsFullSop) {
      if (fetchedSopRequestKeyRef.current === requestKey) return;
      fetchedSopRequestKeyRef.current = requestKey;
      fetchSOPById(sopId).then(() => {
        refreshSelectedSOP();
        const latestId = getSopById(sopId)?.getLatestVersion()?.versionId;
        if (latestId && !versionIdParam) {
          applyDefaultVersionToUrl(latestId);
        }
      });
      return;
    }

    if (!versionIdParam) {
      const latestId = getSopById(sopId)?.getLatestVersion()?.versionId;
      if (latestId) applyDefaultVersionToUrl(latestId);
    }
    fetchedSopRequestKeyRef.current = null;
  }, [
    sopId,
    fetchSOPById,
    setSelectedSOP,
    getSopById,
    hasUnsavedChanges,
    refreshSelectedSOP,
    versionIdParam,
    versionInContext?.versionId,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!sopId || !selectedSOP || selectedSOP.sopId !== sopId || isSaving)
      return;
    if (hasChanges) return;

    if (Array.isArray(selectedSOP.bom) && selectedSOP.bom.length > 0) {
      const materials = selectedSOP.bom.map((m) => ({
        name: m.name,
        quantity: m.quantity,
      }));
      setLocalMaterials(materials);
    }
  }, [sopId, selectedSOP, isSaving, hasChanges]);

  useEffect(() => {
    if (editor && !editor.isDestroyed)
      editor.setEditable(isEditable && canEdit);
  }, [editor, isEditable, canEdit]);

  useEffect(() => {
    if (!sopId || !versionInContext?.versionId || !feedbackIdParam) return;
    const stepKey = `${sopId}:${versionInContext.versionId}:${TITLE_PAGE_FEEDBACK_STEP_INDEX}`;
    setSelectedFeedbackIdForStep(stepKey, feedbackIdParam);
  }, [
    sopId,
    versionInContext?.versionId,
    feedbackIdParam,
    setSelectedFeedbackIdForStep,
  ]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || !versionInContext?.versionId || !sopId)
      return;
    const key = `${sopId}:${versionInContext.versionId}:${TITLE_PAGE_FEEDBACK_STEP_INDEX}`;
    const items = feedbackByStep[key] ?? [];
    const selectedId = selectedFeedbackIdByStep[key];
    const toHighlight =
      selectedId != null ? items.filter((f) => f.id === selectedId) : items;

    const doc = editor.state.doc;

    const normalizeWs = (s: string) => s.replace(/\s+/g, " ").trim();
    const fullDocNorm = normalizeWs(
      doc.textBetween(0, doc.content.size, " ", " "),
    );

    const findTextInDoc = (
      needle: string,
    ): { from: number; to: number } | null => {
      if (!needle) return null;
      let found: { from: number; to: number } | null = null;
      doc.descendants((node, pos) => {
        if (found) return false;
        if (!node.isText || !node.text) return true;
        const idx = node.text.indexOf(needle);
        if (idx !== -1) {
          const from = pos + idx;
          found = { from, to: from + needle.length };
          return false;
        }
        return true;
      });
      return found;
    };

    const orphanedIds: string[] = items
      .filter((f) => f.selectionFrom != null)
      .filter((f) => f.selectedText && f.selectedText.trim() !== "")
      .filter((f) => {
        const raw = f.selectedText!;
        if (findTextInDoc(raw) != null) return false;
        const n = normalizeWs(raw);
        if (n && fullDocNorm.includes(n)) return false;
        return true;
      })
      .map((f) => f.id);

    setOrphanedFeedbackIds(key, orphanedIds);

    const resolveHighlightRange = (f: (typeof items)[number]) => {
      if (f.selectionFrom != null && f.selectionTo != null) {
        const from = f.selectionFrom;
        const to = f.selectionTo;
        if (from < to && to <= doc.content.size) {
          const sliceText = doc.textBetween(from, to, "\n", "\n");
          if (!f.selectedText || sliceText === f.selectedText)
            return { from, to };
        }
      }
      if (f.selectedText) return findTextInDoc(f.selectedText);
      return null;
    };

    const validHighlightCandidates = toHighlight
      .filter((f) => f.selectionFrom != null && f.selectionTo != null)
      .filter((f) => !orphanedIds.includes(f.id));

    const showHighlights = true;
    const highlights =
      showHighlights && validHighlightCandidates.length > 0
        ? validHighlightCandidates
            .map((f) => {
              const range = resolveHighlightRange(f);
              if (!range) return null;
              return {
                from: range.from,
                to: range.to,
                feedbackId: f.id,
                selectedText: f.selectedText,
              };
            })
            .filter((x): x is NonNullable<typeof x> => x != null)
        : [];
    setFeedbackHighlights(editor, highlights, selectedId ?? null);
  }, [
    editor,
    feedbackByStep,
    selectedFeedbackIdByStep,
    sopId,
    versionInContext?.versionId,
    setOrphanedFeedbackIds,
  ]);

  useEffect(() => {
    if (!selectedSOP || isSaving) return;
    const normalizeRevisions = (rows: RevisionRow[]) =>
      rows.map((r, i) => ({
        ...r,
        revision: i + 1,
        created_by: r.created_by?.trim() || "Author",
      }));
    const versionInContext = selectedVersion ?? latestVersion;
    const revisions =
      (versionInContext?.revisions != null &&
      versionInContext.revisions.length > 0
        ? versionInContext.revisions
        : null) ??
      (selectedSOP.revisions && selectedSOP.revisions.length > 0
        ? selectedSOP.revisions
        : null);
    if (revisions && revisions.length > 0) {
      setLocalRevisionRows(normalizeRevisions(revisions));
      return;
    }
    setLocalRevisionRows([]);
  }, [
    selectedSOP,
    selectedVersion,
    latestVersion,
    selectedSOP?.revisions,
    selectedVersion?.revisions,
    latestVersion?.revisions,
    isSaving,
  ]);

  if (!selectedSOP || processingStatus.status === ProcessingStatusEnum.QUEUED) {
    if (sopError) {
      return <SopAccessDenied message={sopError} />;
    }
    return (
      <div className="h-screen w-full flex flex-col">
        <Header />
        <div className="h-full w-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-slate-300">Loading SOP...</p>
          </div>
        </div>
      </div>
    );
  }

  if (
    selectedSOP?.processingStatus ===
      ProcessingStatusEnum.PENDING_VERIFICATION &&
    !isSuperAdmin
  ) {
    return (
      <SopAccessDenied message="This SOP is still being prepared. Please try again later." />
    );
  }

  if (
    processingStatus.isProcessing ||
    processingStatus.status === ProcessingStatusEnum.ERROR ||
    processingStatus.status === ProcessingStatusEnum.REGENERATE
  ) {
    return (
      <div className="h-screen flex flex-col">
        <Header />
        <div className="h-full w-full flex items-center justify-center">
          <ProcessingStatus
            isProcessing={processingStatus.isProcessing}
            status={processingStatus.status as ProcessingStatusEnum}
            message={processingStatus.message}
            error={processingStatus.error}
            formattedElapsedTime={processingStatus.formattedElapsedTime}
            formattedProcessingTime={processingStatus.formattedProcessingTime}
          />
        </div>
      </div>
    );
  }

  const trimEmptyParagraphs = (html: string): string => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const paragraphs = tempDiv.querySelectorAll("p");

    for (let i = paragraphs.length - 1; i >= 0; i--) {
      const paragraph = paragraphs[i];
      const textContent = paragraph.textContent?.trim();
      const hasImage = paragraph.querySelector("img") != null;

      if ((!textContent || textContent === "") && !hasImage) {
        paragraph.remove();
      } else {
        break;
      }
    }

    return tempDiv.innerHTML;
  };
  const handleSaveChanges = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!selectedSOP || !sopId || !editor) return;
    const trimmedHtml = trimEmptyParagraphs(editorContent);
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = trimmedHtml;
    const titleElement = tempDiv.querySelector("h1");
    const extractedTitle =
      titleElement?.textContent || selectedSOP.getDisplayTitle();
    const hasAnythingToSave =
      hasChanges ||
      trimmedHtml !== (originalContent || "") ||
      extractedTitle !== selectedSOP.getDisplayTitle() ||
      localMaterials.length > 0 ||
      localRevisionRows.length > 0;
    if (!hasAnythingToSave) return;

    setIsSaving(true);
    try {
      if (extractedTitle !== selectedSOP.getDisplayTitle()) {
        await updateSopTitleById(sopId, extractedTitle);
      }

      const { processedHtml, uploadedImages } =
        await extractAndUploadTitlePageImages(trimmedHtml, sopId);

      if (uploadedImages.length > 0) {
        showToast(
          "Info",
          `Uploaded ${uploadedImages.length} image(s) to S3`,
          "info",
        );
      }

      const coverImageUrl = extractCoverImageUrl(processedHtml);

      const titlePageContent: TitlePageContent = {
        details: processedHtml,
        title: extractedTitle,
        subtitle: "Standard Operating Procedure",
        coverImage: coverImageUrl,
      };
      await updateSopTitlePage(sopId, titlePageContent);
      refreshSelectedSOP();

      await updateSopBom(sopId, localMaterials);

      await updateSopRevisions(
        sopId,
        localRevisionRows.map((r, i) => ({ ...r, revision: i + 1 })),
      );

      setOriginalContent(trimmedHtml);
      setHasChanges(false);

      showToast("Success", "Title page changes saved successfully!", "success");

      const stepsVersionId = getSopById(sopId)?.getLatestVersion()?.versionId;
      const stepsSearch =
        stepsVersionId != null && stepsVersionId !== ""
          ? `?versionId=${encodeURIComponent(stepsVersionId)}`
          : "";
      navigate(`/sop/${sopId}/steps${stepsSearch}`, {
        state: fromPendingVerification
          ? { fromPendingVerification: true }
          : undefined,
      });
    } catch (error) {
      console.error("Error saving title page changes:", error);
      showToast("Error", "Failed to save changes. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNavigateToSteps = () => {
    if (!sopId) return;
    const stepsVersionId =
      versionInContext?.versionId ??
      getSopById(sopId)?.getLatestVersion()?.versionId;
    const stepsSearch =
      stepsVersionId != null && stepsVersionId !== ""
        ? `?versionId=${encodeURIComponent(stepsVersionId)}`
        : "";
    navigate(`/sop/${sopId}/steps${stepsSearch}`, {
      state: fromPendingVerification
        ? { fromPendingVerification: true }
        : undefined,
    });
  };

  const updateMaterial = (
    index: number,
    field: "name" | "quantity",
    value: string | number,
  ) => {
    setLocalMaterials((prev) => {
      const next = prev.map((m, i) =>
        i === index
          ? { ...m, [field]: field === "quantity" ? Number(value) || 0 : value }
          : m,
      );
      return next;
    });
    setHasChanges(true);
  };

  const addMaterialRow = () => {
    setLocalMaterials((prev) => [...prev, { name: "", quantity: 0 }]);
    setHasChanges(true);
  };

  const removeMaterialRow = (index: number) => {
    setLocalMaterials((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const updateRevisionRow = (
    index: number,
    field: keyof RevisionRow,
    value: string | number,
  ) => {
    setLocalRevisionRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
    setHasChanges(true);
  };

  const addRevisionRow = () => {
    const today = new Date().toISOString().slice(0, 10);
    setLocalRevisionRows((prev) =>
      [
        ...prev,
        {
          revision: prev.length + 1,
          description: "",
          created_at: today,
          created_by: "—",
        },
      ].map((r, i) => ({ ...r, revision: i + 1 })),
    );
    setHasChanges(true);
  };

  const removeRevisionRow = (index: number) => {
    setLocalRevisionRows((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((r, i) => ({ ...r, revision: i + 1 })),
    );
    setHasChanges(true);
  };

  return (
    <div className="relative h-screen flex">
      <Header />

      <div className="h-full pt-20 w-full flex">
        {/* Left aside – form with Revision history & Materials tabs */}
        <TitlePageAside
          activeTab={titlePageAsideTab}
          onTabChange={setTitlePageAsideTab}
          materialsContent={
            <MaterialsTabContent
              localMaterials={localMaterials}
              addMaterialRow={addMaterialRow}
              updateMaterial={updateMaterial}
              removeMaterialRow={removeMaterialRow}
              readOnly={!isEditable || !canEditContent}
              canEdit={canEditContent}
            />
          }
          revisionsContent={
            <RevisionsTabContent
              localRevisionRows={localRevisionRows}
              addRevisionRow={addRevisionRow}
              updateRevisionRow={updateRevisionRow}
              removeRevisionRow={removeRevisionRow}
              readOnly={!isEditable || !canEditContent}
              canEdit={canEditContent}
            />
          }
          versionsContent={
            <VersionsTabContent
              sopId={sopId ?? ""}
              versionsNewestFirst={versionsNewestFirst}
              selectedVersionId={selectedVersion?.versionId}
              versionSwitchLoading={versionSwitchLoading}
              onVersionSelect={handleVersionSelect}
            />
          }
        />
        {/* Main content – editor */}
        <div className="flex-1 min-w-0 flex flex-col relative">
          {isViewingDoneVersion && (
            <ReleasedVersionBanner
              selectedVersion={selectedVersion}
              hasDraftVersion={hasDraftVersion}
              draftVersionId={draftVersionId}
              hasSopId={Boolean(sopId)}
              versionSwitchLoading={versionSwitchLoading}
              isCreatingNewDraft={isCreatingNewDraft}
              onOpenDraft={handleVersionSelect}
              onCreateDraft={async () => {
                if (!sopId) return;
                setIsCreatingNewDraft(true);
                try {
                  await createNewDraft(sopId);
                  refreshSelectedSOP();
                  showToast(
                    "New version created",
                    "You can now edit the new draft version.",
                    "success",
                  );
                } catch (e) {
                  console.error("Create new draft failed:", e);
                  showToast(
                    "Error",
                    "Failed to create a new version. Please try again.",
                    "error",
                  );
                } finally {
                  setIsCreatingNewDraft(false);
                }
              }}
            />
          )}
          <div className="w-full flex flex-row gap-6 max-w-4xl mx-auto justify-center py-4 px-6 flex-1">
            <div className="flex-1">
              {/* Editor Toolbar – hidden when read-only */}
              {isEditable && canEditContent && isSuperAdmin && (
                <div className="flex justify-center mb-6">
                  <EnhancedEditorToolbar
                    editor={editor}
                    onImageUpload={handleImageUpload}
                  />
                </div>
              )}
        

              {/* TipTap Editor */}
              <div className="mt-14 min-h-[500px] bg-gradient-to-br from-gray-900/50 to-black/50 border-2 border-blue-500/30 rounded-xl p-8 focus-within:border-blue-500/50 transition-all duration-200 shadow-2xl">
                <EditorContent
                  editor={editor}
                  className="prose prose-invert max-w-none focus:outline-none"
                  style={{
                    minHeight: "500px",
                    color: "white",
                    fontSize: "16px",
                    lineHeight: "1.6",
                  }}
                />
                {editor && <LinkBubbleMenu editor={editor} />}
                {sopId && editor && (
                  <FeedbackBubbleMenu
                    editor={editor}
                    sopId={sopId}
                    sopTitle={selectedSOP.getDisplayTitle()}
                    versionId={versionInContext?.versionId}
                    stepIndex={TITLE_PAGE_FEEDBACK_STEP_INDEX}
                    unsavedChanges={hasChanges}
                  />
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-center gap-4 pt-4 border-t border-blue-500/20">
                <button
                  onClick={() => navigate(-1)}
                  className="group p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-gray-600 hover:via-gray-500 hover:to-gray-600 shadow-lg shadow-gray-500/30 hover:shadow-lg hover:shadow-gray-500/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer rounded-xl"
                  type="button"
                >
                  <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-xl px-6 py-4 flex items-center gap-2">
                    <ArrowLeft className="w-5 h-5 text-gray-400 group-hover:text-gray-300" />
                    <span className="text-white font-bold text-lg tracking-wide group-hover:text-gray-100 transition-colors duration-300">
                      Back
                    </span>
                  </div>
                </button>

                {hasChanges && isEditable && canEditContent && (
                  <button
                    type="button"
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                    className="group p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-green-600 hover:via-emerald-500 hover:to-green-600 shadow-lg shadow-green-500/30 hover:shadow-lg hover:shadow-green-500/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer rounded-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-xl px-8 py-4 flex items-center gap-2">
                      <Save className="w-5 h-5 text-green-400 group-hover:text-green-300" />
                      <span className="text-white font-bold text-lg tracking-wide group-hover:text-green-100 transition-colors duration-300">
                        {isSaving ? "Saving..." : "Save Changes"}
                      </span>
                    </div>
                  </button>
                )}

                {canAccessRMA && rmaEnabled !== false && (
                  <button
                    type="button"
                    onClick={() => navigate(`/sop/${sopId}/rma`)}
                    className="group p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-cyan-600 hover:via-blue-500 hover:to-cyan-600 shadow-lg shadow-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer rounded-xl"
                  >
                    <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-xl px-6 py-4 flex items-center gap-2">
                      <ScanSearch className="w-5 h-5 text-cyan-300 group-hover:text-cyan-100" />
                      <span className="text-white font-bold text-lg tracking-wide group-hover:text-cyan-100 transition-colors duration-300">
                        RMA Defect Analysis
                      </span>
                    </div>
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleNavigateToSteps}
                  className="group p-[2px] bg-gradient-to-br from-gray-900/95 to-black/95 hover:bg-gradient-to-r hover:from-[#3C6AC6] hover:via-[#D216CC] hover:to-[#FB6B96] shadow-lg shadow-blue-500/30 hover:shadow-lg hover:shadow-blue-500/20 hover:-translate-y-1 transition-all duration-300 cursor-pointer rounded-xl"
                >
                  <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-xl px-8 py-4">
                    <span className="text-white font-bold text-lg tracking-wide group-hover:text-blue-100 transition-colors duration-300">
                      View SOP
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Right aside – reference panel (files + suggestions) */}
        <ReferencePanel
          sopId={sopId ?? undefined}
          versionId={versionInContext?.versionId ?? null}
          stepSegments={stepSegments}
          onSelectFeedback={(feedback) => {
            if (!sopId || !versionInContext?.versionId) return;
            if (feedback.stepIndex === TITLE_PAGE_FEEDBACK_STEP_INDEX) {
              const titleKey = `${sopId}:${versionInContext.versionId}:${TITLE_PAGE_FEEDBACK_STEP_INDEX}`;
              setSelectedFeedbackIdForStep(titleKey, null);
              navigate(
                `/sop/${sopId}/steps?step=0&versionId=${encodeURIComponent(versionInContext.versionId)}`,
              );
              return;
            }
            navigate(
              `/sop/${sopId}/steps?step=${feedback.stepIndex}&versionId=${encodeURIComponent(versionInContext.versionId)}&feedbackId=${encodeURIComponent(feedback.id)}`,
            );
          }}
        />
      </div>
    </div>
  );
};

type ReleasedVersionBannerInfo = {
  editedAt?: string | null;
  editorEmail?: string | null;
} | null;

export const ReleasedVersionBanner: FC<{
  selectedVersion: ReleasedVersionBannerInfo;
  hasDraftVersion: boolean;
  draftVersionId?: string;
  hasSopId: boolean;
  versionSwitchLoading: boolean;
  isCreatingNewDraft: boolean;
  onOpenDraft: (versionId: string) => void;
  onCreateDraft: () => Promise<void>;
}> = ({
  selectedVersion,
  hasDraftVersion,
  draftVersionId,
  hasSopId,
  versionSwitchLoading,
  isCreatingNewDraft,
  onOpenDraft,
  onCreateDraft,
}) => (
  <div className="absolute top-0 right-0 w-full px-2.5 py-2.5 flex items-center justify-between bg-zinc-900/50 backdrop-blur-sm border-b border-zinc-700/50">
    <div className="flex text-xs">
      <div className="flex items-center justify-center border p-2.5 border-amber-500/30 rounded">
        <Lock className="w-4 h-4 text-amber-400" />
      </div>
      <div className="flex flex-col pl-2">
        <p className="font-medium text-base text-amber-400/90">
          This version is released and cannot be edited
        </p>
        <span className="text-xs text-zinc-300/40">
          Released{" "}
          {selectedVersion?.editedAt
            ? new Date(selectedVersion.editedAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "--"}{" "}
          by {selectedVersion?.editorEmail || "--"}
        </span>
      </div>
    </div>
    {hasDraftVersion && draftVersionId ? (
      <button
        type="button"
        disabled={!hasSopId || versionSwitchLoading}
        onClick={() => onOpenDraft(draftVersionId)}
        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs border cursor-pointer bg-amber-500/15 border-amber-500/30 text-amber-300 hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {versionSwitchLoading ? (
          <>
            <Loader className="w-3.5 h-3.5 animate-spin" />
            Switching...
          </>
        ) : (
          <>
            <ArrowRight className="w-3.5 h-3.5" />
            Open draft to edit
          </>
        )}
      </button>
    ) : (
      <button
        type="button"
        disabled={!hasSopId || isCreatingNewDraft}
        onClick={onCreateDraft}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCreatingNewDraft ? (
          <>
            <Loader className="w-3.5 h-3.5 animate-spin" />
            Creating...
          </>
        ) : (
          "Create new version"
        )}
      </button>
    )}
  </div>
);

/** Version list tab: number, title, description (for loaded), badges (Draft / Latest release), current highlight */
const VersionsTabContent: FC<{
  sopId: string;
  versionsNewestFirst: VersionListItem[];
  selectedVersionId: string | undefined;
  versionSwitchLoading: boolean;
  onVersionSelect: (versionId: string) => void;
}> = ({
  versionsNewestFirst,
  selectedVersionId,
  versionSwitchLoading,
  onVersionSelect,
}) => {
  const latestDoneVersionNumber = useMemo(() => {
    const done = versionsNewestFirst.find((v) => v.status === "done");
    return done?.versionNumber;
  }, [versionsNewestFirst]);

  if (versionsNewestFirst.length === 0) {
    return (
      <p className="text-slate-500 text-xs py-2">
        No versions yet. Versions appear after you release from the Steps view.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {versionsNewestFirst.map((ver) => {
        const isSelected = ver.versionId === selectedVersionId;
        const isDraft = ver.status === "draft";
        const isLatestDone =
          ver.status === "done" &&
          ver.versionNumber === latestDoneVersionNumber;
        const description = ver.sopDescription ?? "—";

        return (
          <button
            key={ver.versionId}
            type="button"
            disabled={versionSwitchLoading || isSelected}
            onClick={() => onVersionSelect(ver.versionId)}
            className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
              isSelected
                ? "border-blue-500/50 bg-blue-500/10 text-slate-100"
                : "border-slate-600/50 bg-slate-800/40 text-slate-300 hover:border-slate-500 hover:bg-slate-700/40"
            } ${versionSwitchLoading ? "pointer-events-none opacity-75" : ""}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-200 tabular-nums">
                v{ver.versionNumber}
              </span>
              {isDraft && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  Draft
                </span>
              )}
              {isLatestDone && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/30">
                  Latest release
                </span>
              )}
            </div>
            <div className="text-sm font-medium text-slate-200 mt-0.5 truncate">
              {ver.title || "Untitled"}
            </div>
            <div className="text-xs text-slate-500 mt-1 line-clamp-2">
              {description}
            </div>
          </button>
        );
      })}
    </div>
  );
};

/** Left aside: metadata (Revision history, Materials, Versions), collapsible */
const TitlePageAside: FC<{
  activeTab: "materials" | "revisions" | "versions";
  onTabChange: (tab: "materials" | "revisions" | "versions") => void;
  materialsContent: React.ReactNode;
  revisionsContent: React.ReactNode;
  versionsContent: React.ReactNode;
}> = ({
  activeTab,
  onTabChange,
  materialsContent,
  revisionsContent,
  versionsContent,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className="shrink-0 border-r border-slate-600/50 bg-slate-800/40 flex flex-col relative overflow-hidden transition-[width] duration-200 ease-in-out"
      style={{ width: collapsed ? 40 : 384 }}
      title="Metadata (Revision history, Materials, Versions)"
    >
      <div
        className={`absolute inset-0 flex h-full flex-col items-center py-4 transition-opacity duration-150 ease-out ${
          collapsed
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="p-2 rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 transition-colors"
          aria-label="Expand metadata"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <span className="text-[10px] text-slate-500 mt-2 [writing-mode:vertical-rl] rotate-180 select-none">
          Metadata
        </span>
      </div>
      <div
        className={`h-full flex flex-col transition-opacity duration-150 ease-out ${
          collapsed
            ? "opacity-0 pointer-events-none"
            : "opacity-100 pointer-events-auto"
        }`}
      >
        <div className="px-3 pb-2 border-b border-slate-600/50 flex items-center justify-between gap-2 pt-4">
          <div className="flex rounded-lg bg-slate-900/60 p-0.5 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => onTabChange("versions")}
              className={`flex-1 rounded-md px-1 py-1 text-sm font-medium transition-colors min-w-0 ${
                activeTab === "versions"
                  ? "bg-slate-700 text-slate-100 shadow"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Versions
            </button>
            <button
              type="button"
              onClick={() => onTabChange("revisions")}
              className={`flex-1 rounded-md px-1 py-1 text-sm font-medium transition-colors min-w-0 ${
                activeTab === "revisions"
                  ? "bg-slate-700 text-slate-100 shadow"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Revisions
            </button>
            <button
              type="button"
              onClick={() => onTabChange("materials")}
              className={`flex-1 rounded-md px-1 py-1 text-sm font-medium transition-colors min-w-0 ${
                activeTab === "materials"
                  ? "bg-slate-700 text-slate-100 shadow"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Materials
            </button>
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="p-1.5 rounded-md text-slate-500 hover:text-slate-200 hover:bg-slate-700/50 transition-colors shrink-0"
            aria-label="Collapse metadata"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-3 py-3">
          {activeTab === "revisions" && revisionsContent}
          {activeTab === "materials" && materialsContent}
          {activeTab === "versions" && versionsContent}
        </div>
      </div>
    </aside>
  );
};

const MaterialsTabContent: FC<{
  localMaterials: TitlePageMaterial[];
  addMaterialRow: () => void;
  updateMaterial: (
    index: number,
    field: "name" | "quantity",
    value: string | number,
  ) => void;
  removeMaterialRow: (index: number) => void;
  readOnly?: boolean;
  canEdit?: boolean;
}> = ({
  localMaterials,
  addMaterialRow,
  updateMaterial,
  removeMaterialRow,
  readOnly = false,
  canEdit = true,
}) => (
  <div className="space-y-2">
    <table className="w-full text-sm text-left">
      <thead>
        <tr className="border-b border-slate-600/50 text-slate-500">
          <th className="py-1.5 pr-2 font-medium">Name</th>
          <th className="py-1.5 font-medium text-right w-14">Qty</th>
          {!readOnly && <th className="w-8" />}
        </tr>
      </thead>
      <tbody>
        {localMaterials.length === 0 ? (
          <tr>
            <td
              colSpan={readOnly ? 2 : 3}
              className="py-3 text-slate-500 text-xs"
            >
              No materials.{readOnly ? "" : ' Click "Add row" to add.'}
            </td>
          </tr>
        ) : (
          localMaterials.map((m, i) => (
            <tr key={i} className="border-b border-slate-700/50 last:border-0">
              <td className="py-1 pr-2">
                <input
                  type="text"
                  value={m.name}
                  onChange={(e) => updateMaterial(i, "name", e.target.value)}
                  placeholder="Name"
                  disabled={readOnly || !canEdit}
                  readOnly={readOnly || !canEdit}
                  className="w-full bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:opacity-90 disabled:cursor-default read-only:bg-slate-800/80"
                />
              </td>
              <td>
                <input
                  type="number"
                  min={0}
                  value={m.quantity}
                  onChange={(e) =>
                    updateMaterial(
                      i,
                      "quantity",
                      e.target.value === ""
                        ? 0
                        : parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={readOnly || !canEdit}
                  readOnly={readOnly || !canEdit}
                  className="w-full max-w-[4rem] bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-sm text-right focus:outline-none focus:ring-1 focus:ring-slate-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-90 disabled:cursor-default"
                />
              </td>
              {!readOnly && canEdit && (
                <td className="py-1 pl-0">
                  <button
                    type="button"
                    onClick={() => removeMaterialRow(i)}
                    className="p-1 text-slate-500 hover:text-red-400 rounded"
                    aria-label="Remove row"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              )}
            </tr>
          ))
        )}
        {!readOnly && canEdit && (
          <tr>
            <td colSpan={3} className="py-2">
              <button
                type="button"
                onClick={addMaterialRow}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 rounded px-2 py-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add row
              </button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const RevisionsTabContent: FC<{
  localRevisionRows: RevisionRow[];
  addRevisionRow: () => void;
  updateRevisionRow: (
    index: number,
    field: keyof RevisionRow,
    value: string | number,
  ) => void;
  removeRevisionRow: (index: number) => void;
  readOnly?: boolean;
  canEdit?: boolean;
}> = ({
  localRevisionRows,
  addRevisionRow,
  updateRevisionRow,
  removeRevisionRow,
  readOnly = false,
  canEdit = false,
}) => (
  <div className="space-y-3">
    {localRevisionRows.length === 0 ? (
      <p className="text-slate-500 text-xs py-2">
        No revisions.{readOnly ? "" : ' Click "Add row" to add.'}
      </p>
    ) : (
      localRevisionRows.map((row, i) => (
        <div key={i} className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="text-slate-300 text-sm font-medium tabular-nums shrink-0"
              title="Revision number (1-based position)"
            >
              Revision {i + 1}
            </span>

            {!readOnly && canEdit && (
              <button
                type="button"
                onClick={() => removeRevisionRow(i)}
                className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-slate-700/50 rounded shrink-0 ml-auto"
                aria-label="Remove revision"
                title="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-2">
            <label htmlFor={`created_by_${i}`} className="flex flex-col flex-1">
              Created by:
              <input
                type="text"
                id={`created_by_${i}`}
                value={row.created_by}
                onChange={(e) =>
                  updateRevisionRow(i, "created_by", e.target.value)
                }
                placeholder="By"
                disabled={readOnly || !canEdit}
                readOnly={readOnly || !canEdit}
                className="flex-1 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:opacity-90 disabled:cursor-default"
              />
            </label>

            <label htmlFor={`created_at_${i}`} className="w-[120px]">
              Created at:
              <input
                type="date"
                id={`created_at_${i}`}
                value={
                  row.created_at.includes("T")
                    ? row.created_at.slice(0, 10)
                    : row.created_at
                }
                onChange={(e) => {
                  const dateValue = e.target.value;
                  const isoValue = dateValue.includes("T")
                    ? dateValue
                    : `${dateValue}T00:00:00Z`;
                  updateRevisionRow(i, "created_at", isoValue);
                }}
                disabled={readOnly || !canEdit}
                readOnly={readOnly || !canEdit}
                className="w-[120px] bg-slate-800 border border-slate-600 rounded px-2 py-1 text-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:opacity-90 disabled:cursor-default"
              />
            </label>
          </div>

          <textarea
            value={row.description}
            onChange={(e) =>
              updateRevisionRow(i, "description", e.target.value)
            }
            placeholder="Description (required)"
            rows={2}
            disabled={readOnly || !canEdit}
            readOnly={readOnly || !canEdit}
            className="w-full resize-y min-h-[2.5rem] bg-slate-800 border border-slate-600 rounded px-2 py-1.5 text-slate-200 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 placeholder:text-slate-500 disabled:opacity-90 disabled:cursor-default"
          />
          {i < localRevisionRows.length - 1 && (
            <div className="border-t border-slate-600/50 my-2" aria-hidden />
          )}
        </div>
      ))
    )}
    {!readOnly && canEdit && (
      <button
        type="button"
        onClick={addRevisionRow}
        className="w-full flex items-center justify-center gap-1 text-xs text-slate-400 hover:text-slate-200 rounded px-2 py-1.5 mt-1"
      >
        <Plus className="w-3.5 h-3.5" /> Add row
      </button>
    )}
  </div>
);

export default SOPTitle;
